"use client";

import { CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import CategoryForm from "@/components/templates/CategoryForm";
import {
  buildFormSchema,
  buildInitialData,
  normalizeCreation,
  normalizeUpdate,
} from "@/components/templates/CategoryForm/form-data";
import { CategoryFormData } from "@/components/templates/CategoryForm/types";
import PageLayout from "@/components/templates/PageLayout";
import { MAX_CATEGORY_HIERARCHY_LEVEL } from "@/constants/products.constants";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useToast from "@/contexts/toast-context";
import useCreateCategory from "@/hooks/requests/category/use-create-category";
import useGetCategoryById from "@/hooks/requests/category/use-get-category-by-id";
import useUpdateCategory from "@/hooks/requests/category/use-update-category";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useMutate from "@/hooks/use-mutate";
import { ProductCategoryFormPageMessages } from "@/messages/product-categories/product-categories-form-page.messages";
import categoryService from "@/services/category.service";

const CATEGORY_FETCH_CONFIG = {
  pageNumber: 1,
  pageSize: 1000,
} as const;

const ROOT_CATEGORY_LEVEL = 1;
const EMPTY_PARENT_CODE = "";
const EMPTY_PARENT_ID = null;
const DEFAULT_CATEGORY_ID = 0;
const DEFAULT_CUSTOMER_ID = 0;

const ERROR_MESSAGES = {
  moveCategory: "mover esta categoria para",
  createSubcategory: "criar subcategorias neste",
  hierarchyLimitExceeded: (maxLevel: number) =>
    `nível. Limite máximo de ${maxLevel} níveis atingido.`,
  parentCategoryNotFound: "Erro ao buscar categoria pai.",
} as const;

type CategoryResult =
  | { error: false; level: number; parentId: number | null; parentCode: string }
  | { error: true; message: string };

type Props = {
  messages: ProductCategoryFormPageMessages;
  isEditing?: boolean;
};

export default function CategoryFormPage({ messages, isEditing = false }: Props) {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const router = useNavigate();
  const { mutate } = useMutate();
  const { showToast } = useToast();
  const customerId = useGetCustomerId();
  const categoryIdNumber = Number(categoryId) || DEFAULT_CATEGORY_ID;
  const [isLoadingParent, setIsLoadingParent] = useState(false);

  const parentCode = searchParams.get("parentCode");

  const { create, isLoading: isLoadingCreate } = useCreateCategory();
  const { update, isLoading: isLoadingUpdate } = useUpdateCategory();
  const {
    data,
    isLoading: isLoadingCategory,
    error,
  } = useGetCategoryById({
    _id: categoryIdNumber,
  });

  const buildHierarchyErrorMessage = useCallback((isEditingMode: boolean) => {
    const actionMessage = isEditingMode
      ? ERROR_MESSAGES.moveCategory
      : ERROR_MESSAGES.createSubcategory;

    return `Não é possível ${actionMessage} ${ERROR_MESSAGES.hierarchyLimitExceeded(MAX_CATEGORY_HIERARCHY_LEVEL)}`;
  }, []);

  const createRootCategoryData = useCallback(
    (): CategoryResult => ({
      error: false,
      level: ROOT_CATEGORY_LEVEL,
      parentId: EMPTY_PARENT_ID,
      parentCode: EMPTY_PARENT_CODE,
    }),
    []
  );

  const createParentCategoryData = useCallback(
    (foundParent: { level: number; id: number; code: string }): CategoryResult => ({
      error: false,
      level: foundParent.level + 1,
      parentId: foundParent.id,
      parentCode: foundParent.code,
    }),
    []
  );

  const createErrorData = useCallback(
    (errorMessage: string): CategoryResult => ({
      error: true,
      message: errorMessage,
    }),
    []
  );

  const resolveParentCategoryFields = useCallback(
    async (parentCode: string): Promise<CategoryResult> => {
      const hasValidParentCode = parentCode && parentCode.trim() !== EMPTY_PARENT_CODE;

      if (!hasValidParentCode) {
        return createRootCategoryData();
      }

      const allCategories = await categoryService.getAll(CATEGORY_FETCH_CONFIG);
      const foundParent = allCategories.items.find((cat) => cat.code === parentCode);

      if (!foundParent) {
        return createRootCategoryData();
      }

      const isHierarchyLimitExceeded = foundParent.level >= MAX_CATEGORY_HIERARCHY_LEVEL;

      if (isHierarchyLimitExceeded) {
        const errorMessage = buildHierarchyErrorMessage(isEditing);
        return createErrorData(errorMessage);
      }

      return createParentCategoryData(foundParent);
    },
    [
      isEditing,
      buildHierarchyErrorMessage,
      createRootCategoryData,
      createParentCategoryData,
      createErrorData,
    ]
  );

  const handleParentCategoryError = useCallback(() => {
    showToast({
      text: ERROR_MESSAGES.parentCategoryNotFound,
      type: "error",
    });
    router(ROUTE_URLS.productCategories(lang));
  }, [showToast, router, lang]);

  useEffect(() => {
    const fetchParentCategory = async () => {
      const shouldFetchParent = parentCode && !isEditing;

      if (!shouldFetchParent) {
        return;
      }

      setIsLoadingParent(true);

      try {
        const result = await resolveParentCategoryFields(parentCode);

        if (result.error) {
          showToast({
            text: result.message,
            type: "error",
          });
          router(ROUTE_URLS.productCategories(lang));
        }
      } catch (error) {
        handleParentCategoryError();
      } finally {
        setIsLoadingParent(false);
      }
    };

    fetchParentCategory();
  }, [
    parentCode,
    isEditing,
    showToast,
    lang,
    router,
    resolveParentCategoryFields,
    handleParentCategoryError,
  ]);

  const onSuccess = () => {
    mutate(SWR_KEYS.getCategoryById);
    mutate(SWR_KEYS.getCategories);
    router(ROUTE_URLS.productCategories(lang));
  };

  const handleUpdate = async (formData: CategoryFormData) => {
    const normalizedFormData = normalizeUpdate(formData);

    const originalParentCode = data?.parentCode ?? EMPTY_PARENT_CODE;
    const newParentCode = normalizedFormData.parentCode ?? EMPTY_PARENT_CODE;
    const hasParentCodeChanged = originalParentCode !== newParentCode;

    if (hasParentCodeChanged) {
      const result = await resolveParentCategoryFields(newParentCode);

      if (result.error) {
        showToast({
          text: result.message,
          type: "error",
        });
        return;
      }

      Object.assign(normalizedFormData, result);
    }

    const successMessage = messages.categoryUpdateSuccess;
    const errorMessage = messages.categoryUpdateError;

    update({
      formData: normalizedFormData,
      successMessage,
      errorMessage,
      onSuccess,
      _id: categoryIdNumber,
    });
  };

  const handleCreate = async (formData: CategoryFormData) => {
    const normalizedFormData = normalizeCreation(formData);

    const result = await resolveParentCategoryFields(
      normalizedFormData.parentCode ?? EMPTY_PARENT_CODE
    );

    if (result.error) {
      showToast({
        text: result.message,
        type: "error",
      });
      return;
    }

    Object.assign(normalizedFormData, result);

    const successMessage = messages.categoryCreateSuccess;
    const errorMessage = messages.categoryCreateError;

    create({ formData: normalizedFormData, successMessage, errorMessage, onSuccess });
  };

  const handleSubmitForm = async (formData: CategoryFormData) => {
    if (!user) return;

    if (isEditing) handleUpdate(formData);
    if (!isEditing) handleCreate(formData);
  };

  const isLoadingSubmit = isEditing ? isLoadingUpdate : isLoadingCreate;
  const pageTitle = isEditing ? messages.updatePageTitle : messages.createPageTitle;

  const shouldLoadForm = isEditing ? !!data && !error : !isLoadingParent;
  const shouldLoadError = !data && !!error && !isLoadingCategory;

  const breadCrumbs = { title: messages.breadCrumbTitle, href: ROUTE_URLS.productCategories(lang) };
  const BREAD_CRUMBS = [breadCrumbs];

  const getInitialData = () => {
    const baseData = buildInitialData(Number(customerId) || DEFAULT_CUSTOMER_ID, isEditing, data);
    if (!isEditing && parentCode) {
      baseData.parentCode = parentCode;
    }
    return baseData;
  };

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense
          fallback={<CircularProgress />}
          isSuspended={isLoadingCategory || isLoadingParent}
        >
          {shouldLoadForm && (
            <Formik
              initialValues={getInitialData()}
              validationSchema={buildFormSchema(messages)}
              validateOnChange={false}
              onSubmit={handleSubmitForm}
            >
              <CategoryForm
                messages={messages}
                isLoading={isLoadingSubmit}
                defaultParentCode={parentCode}
              />
            </Formik>
          )}
          {shouldLoadError && (
            <AlertBlock
              title={messages.errorToLoad}
              description={error?.message || ""}
              alt={messages.notFoundAlt}
            />
          )}

          {!data && !!error && (
            <AlertBlock
              title={messages.errorToLoad}
              description={error.message}
              alt={messages.notFoundAlt}
            />
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
